import { ReactNode, createContext, useContext } from "react";
import _ from "lodash";
import {
  useQuery,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import type { User } from "@db/schema";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, RegisterData>;
};

type LoginData = {
  email: string;
  password: string;
};

type RegisterData = {
  email: string;
  password: string;
  fullName?: string;
  type: "fan" | "artist";
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null, Error>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      const res = await fetch("/api/user", {
        credentials: "include"
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error("Failed to fetch user");
      const userData = await res.json();

      if (!userData) return null;

      return {
        ...userData,
        roles: _.compact(_.toArray(_.get(userData, 'roles', [])))
      };
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 5000,
    gcTime: 10000,
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
        credentials: "include"
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: async (userData) => {
      // Format the user data the same way as the query
      const formattedUser = {
        ...userData,
        roles: _.compact(_.toArray(_.get(userData, 'roles', [])))
      };
      
      // Set the query data
      queryClient.setQueryData(["/api/user"], formattedUser);
      
      // Invalidate and refetch to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      
      // Navigate after the query has been updated
      setTimeout(() => {
        setLocation("/");
      }, 100);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include"
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
      return res.json();
    },
    onSuccess: async (userData) => {
      // Format the user data the same way as the query
      const formattedUser = {
        ...userData,
        roles: _.compact(_.toArray(_.get(userData, 'roles', [])))
      };
      
      // Set the query data
      queryClient.setQueryData(["/api/user"], formattedUser);
      
      // Invalidate and refetch to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      
      toast({
        title: "Success",
        description: "Your account has been created",
      });
      
      // Navigate after the query has been updated
      setTimeout(() => {
        setLocation("/");
      }, 100);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/logout", { 
        method: "POST",
        credentials: "include"
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      setLocation("/login");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}